import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getAutomations = createAsyncThunk(
    'automations/getAutomations',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching automations with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.AUTOMATIONS, { params });
            console.log('Automations response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automations retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch automations',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get automations error:', error);

            let errorMessage = 'Failed to fetch automations';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch automations';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getAutomation = createAsyncThunk(
    'automations/getAutomation',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching automation:', id);

            const response = await apiClient.get(API_ENDPOINTS.AUTOMATION(id));
            console.log('Automation response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automation retrieved successfully',
                    automation: data?.automation || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch automation',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get automation error:', error);

            let errorMessage = 'Failed to fetch automation';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch automation';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createAutomation = createAsyncThunk(
    'automations/createAutomation',
    async (automationData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating automation...');
            console.log('Automation data:', automationData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_AUTOMATION, automationData);
            console.log('Create automation response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Automation created successfully',
                    automation: data?.automation || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create automation',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create automation error:', error);

            let errorMessage = 'Failed to create automation';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create automation';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateAutomation = createAsyncThunk(
    'automations/updateAutomation',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating automation:', id);
            console.log('Update data:', data);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_AUTOMATION(id), data);
            console.log('Update automation response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automation updated successfully',
                    automation: responseData?.automation || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update automation',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update automation error:', error);

            let errorMessage = 'Failed to update automation';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update automation';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteAutomation = createAsyncThunk(
    'automations/deleteAutomation',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting automation:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_AUTOMATION(id));
            console.log('Delete automation response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automation deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete automation',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete automation error:', error);

            let errorMessage = 'Failed to delete automation';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete automation';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const toggleAutomation = createAsyncThunk(
    'automations/toggleAutomation',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Toggling automation:', id);

            const response = await apiClient.post(API_ENDPOINTS.TOGGLE_AUTOMATION(id));
            console.log('Toggle automation response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automation toggled successfully',
                    automation: data?.automation || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to toggle automation',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Toggle automation error:', error);

            let errorMessage = 'Failed to toggle automation';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to toggle automation';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const runAutomation = createAsyncThunk(
    'automations/runAutomation',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Running automation:', id);

            const response = await apiClient.post(API_ENDPOINTS.RUN_AUTOMATION(id));
            console.log('Run automation response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automation run successfully',
                    result: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to run automation',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Run automation error:', error);

            let errorMessage = 'Failed to run automation';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to run automation';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getAutomationStats = createAsyncThunk(
    'automations/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching automation statistics...');

            const response = await apiClient.get(API_ENDPOINTS.AUTOMATION_STATS, { params });
            console.log('Automation stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automation statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch automation statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get automation stats error:', error);

            let errorMessage = 'Failed to fetch automation statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch automation statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

const initialState = {
    automations: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentAutomation: null,
    stats: null,
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const automationSlice = createSlice({
    name: 'automations',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearAutomationState: (state) => {
            state.automations = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentAutomation = null;
            state.stats = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentAutomation: (state) => {
            state.currentAutomation = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET AUTOMATIONS =====
            .addCase(getAutomations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getAutomations.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.automations = responseData.data || [];
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.stats = responseData.meta?.stats || null;
                state.successMessage = payload.message || 'Automations retrieved successfully';
                state.apiMessage = payload.message || 'Automations retrieved successfully';
                state.error = null;
            })
            .addCase(getAutomations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch automations';
                state.apiMessage = action.payload?.message || 'Failed to fetch automations';
                state.successMessage = null;
                state.automations = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.stats = null;
            })

            // ===== GET AUTOMATION =====
            .addCase(getAutomation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getAutomation.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentAutomation = payload.automation || null;
                state.successMessage = payload.message || 'Automation retrieved successfully';
                state.apiMessage = payload.message || 'Automation retrieved successfully';
                state.error = null;
            })
            .addCase(getAutomation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch automation';
                state.apiMessage = action.payload?.message || 'Failed to fetch automation';
                state.successMessage = null;
                state.currentAutomation = null;
            })

            // ===== CREATE AUTOMATION =====
            .addCase(createAutomation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createAutomation.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Automation created successfully';
                state.apiMessage = payload.message || 'Automation created successfully';
                state.error = null;
                if (payload.automation) {
                    state.automations = [payload.automation, ...state.automations];
                }
            })
            .addCase(createAutomation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create automation';
                state.apiMessage = action.payload?.message || 'Failed to create automation';
                state.successMessage = null;
            })

            // ===== UPDATE AUTOMATION =====
            .addCase(updateAutomation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateAutomation.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Automation updated successfully';
                state.apiMessage = payload.message || 'Automation updated successfully';
                state.error = null;
                if (payload.automation) {
                    state.automations = state.automations.map(a => 
                        a.id === payload.automation.id ? payload.automation : a
                    );
                    if (state.currentAutomation?.id === payload.automation.id) {
                        state.currentAutomation = payload.automation;
                    }
                }
            })
            .addCase(updateAutomation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update automation';
                state.apiMessage = action.payload?.message || 'Failed to update automation';
                state.successMessage = null;
            })

            // ===== DELETE AUTOMATION =====
            .addCase(deleteAutomation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteAutomation.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Automation deleted successfully';
                state.apiMessage = payload.message || 'Automation deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.automations = state.automations.filter(a => a.id !== payload.id);
                    if (state.currentAutomation?.id === payload.id) {
                        state.currentAutomation = null;
                    }
                }
            })
            .addCase(deleteAutomation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete automation';
                state.apiMessage = action.payload?.message || 'Failed to delete automation';
                state.successMessage = null;
            })

            // ===== TOGGLE AUTOMATION =====
            .addCase(toggleAutomation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleAutomation.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Automation toggled successfully';
                state.apiMessage = payload.message || 'Automation toggled successfully';
                state.error = null;
                if (payload.automation) {
                    state.automations = state.automations.map(a => 
                        a.id === payload.automation.id ? payload.automation : a
                    );
                    if (state.currentAutomation?.id === payload.automation.id) {
                        state.currentAutomation = payload.automation;
                    }
                }
            })
            .addCase(toggleAutomation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle automation';
                state.apiMessage = action.payload?.message || 'Failed to toggle automation';
                state.successMessage = null;
            })

            // ===== RUN AUTOMATION =====
            .addCase(runAutomation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(runAutomation.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Automation run successfully';
                state.apiMessage = payload.message || 'Automation run successfully';
                state.error = null;
                // Update sent count in the UI
                if (payload.result) {
                    // You might want to update the automation in the list
                    // This will be handled by refetching automations
                }
            })
            .addCase(runAutomation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to run automation';
                state.apiMessage = action.payload?.message || 'Failed to run automation';
                state.successMessage = null;
            })

            // ===== GET AUTOMATION STATS =====
            .addCase(getAutomationStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getAutomationStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = payload.stats || null;
                state.successMessage = payload.message || 'Automation statistics retrieved successfully';
                state.apiMessage = payload.message || 'Automation statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getAutomationStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch automation statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch automation statistics';
                state.successMessage = null;
                state.stats = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearAutomationState,
    clearCurrentAutomation,
} = automationSlice.actions;

export const selectAutomations = (state) => state.automations?.automations || [];
export const selectAutomationPagination = (state) => state.automations?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentAutomation = (state) => state.automations?.currentAutomation || null;
export const selectAutomationStats = (state) => state.automations?.stats || null;
export const selectAutomationsLoading = (state) => state.automations?.isLoading || false;
export const selectAutomationsError = (state) => state.automations?.error || null;
export const selectAutomationsSuccess = (state) => state.automations?.successMessage || null;
export const selectAutomationsApiMessage = (state) => state.automations?.apiMessage || null;

// ============================================
// COMPOSED SELECTORS
// ============================================
export const selectAutomationStatsSummary = (state) => {
    const stats = selectAutomationStats(state);
    if (!stats) return null;
    return {
        total: stats.total || 0,
        active: stats.active || 0,
        inactive: stats.inactive || 0,
        total_sent: stats.total_sent || 0,
        by_type: stats.by_type || [],
        recent: stats.recent || []
    };
};

export default automationSlice.reducer;